import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X, ExternalLink, Pin, Settings, CalendarClock, Megaphone } from 'lucide-react';
import { useAnnouncements } from '../../context/AnnouncementContext';
import { levelOf, formatDateTime, formatRelative } from './announcementLevels';
import './announcements.css';

const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)"'\]])/g;

// Plain text with line breaks; bare URLs become links.
const Linkified = ({ text }) => (
  <>
    {text.split(URL_RE).map((part, i) =>
      i % 2 === 1 ? (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-300 underline hover:text-blue-200 break-all">
          {part}
        </a>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    )}
  </>
);

const CONFETTI_COLORS = ['#f472b6', '#a78bfa', '#60a5fa', '#34d399', '#fbbf24', '#f87171'];

const Confetti = () => {
  const pieces = useMemo(
    () => Array.from({ length: 70 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 2.2 + Math.random() * 1.8,
      drift: (Math.random() - 0.5) * 240,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      rotate: Math.random() * 360,
    })),
    []
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-20" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="ksp-ann-confetti"
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            transform: `rotate(${p.rotate}deg)`,
            '--ksp-drift': `${p.drift}px`,
          }}
        />
      ))}
    </div>
  );
};

const ListItem = ({ a, selected, onSelect }) => {
  const lvl = levelOf(a.level);
  const { Icon } = lvl;
  return (
    <button
      type="button"
      onClick={() => onSelect(a.id)}
      className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors flex gap-3 items-start
        ${selected
          ? 'bg-slate-700/80 border-slate-500'
          : 'border-transparent hover:bg-slate-700/40'}
        ${a.is_live ? '' : 'opacity-60 hover:opacity-100'}`}
    >
      <span className={`mt-0.5 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${lvl.iconWrap.replace(/ksp-ann-\S+/g, '')}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className={`truncate text-sm ${a.viewed_at ? 'text-slate-300' : 'text-white font-semibold'}`}>{a.title}</span>
          {a.is_pinned && <Pin className="w-3 h-3 text-slate-400 flex-shrink-0" />}
        </span>
        <span className="block text-xs text-slate-400 mt-0.5">{formatRelative(a.published_at)}</span>
      </span>
      {!a.viewed_at && a.is_live && <span className={`mt-2 w-2 h-2 rounded-full flex-shrink-0 ${lvl.dot}`} />}
    </button>
  );
};

const AnnouncementModal = () => {
  const {
    enabled, modal, closeModal, selectInModal, announcements, live, isAdmin, markViewed,
  } = useAnnouncements();
  const [confettiKey, setConfettiKey] = useState(0);

  const past = useMemo(() => announcements.filter((a) => !a.is_live), [announcements]);
  const selected = announcements.find((a) => a.id === modal.selectedId) || live[0] || announcements[0];

  // Opening something counts as viewing it.
  useEffect(() => {
    if (modal.open && selected && !selected.viewed_at && selected.is_live) markViewed(selected.id);
  }, [modal.open, selected, markViewed]);

  useEffect(() => {
    if (modal.open && selected?.level === 'celebration') setConfettiKey((k) => k + 1);
  }, [modal.open, selected?.id, selected?.level]);

  useEffect(() => {
    if (!modal.open) return undefined;
    const ordered = [...live, ...past];
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && selected) {
        const i = ordered.findIndex((a) => a.id === selected.id);
        const next = ordered[i + (e.key === 'ArrowDown' ? 1 : -1)];
        if (next) {
          e.preventDefault();
          selectInModal(next.id);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [modal.open, live, past, selected, closeModal, selectInModal]);

  if (!enabled || !modal.open) return null;

  const lvl = selected ? levelOf(selected.level) : null;

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-6">
      <div className="ksp-ann-backdrop absolute inset-0 bg-slate-950/75 backdrop-blur-sm" onClick={closeModal} />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Announcements"
        className="ksp-ann-pop relative w-full max-w-5xl h-[85vh] sm:h-[78vh] flex flex-col md:flex-row
                   bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden"
      >
        {selected?.level === 'celebration' && <Confetti key={confettiKey} />}

        {/* History */}
        <aside className="md:w-80 flex-shrink-0 flex flex-col border-b md:border-b-0 md:border-r border-slate-700 bg-slate-900/40 max-h-48 md:max-h-none">
          <div className="px-4 py-3 border-b border-slate-700 flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-200 flex-1">Announcements</h2>
            {isAdmin && (
              <Link
                to="/announcements/manage"
                onClick={closeModal}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700"
                title="Manage announcements"
              >
                <Settings className="w-4 h-4" />
              </Link>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {live.length > 0 && (
              <p className="px-2 pt-1 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Active</p>
            )}
            {live.map((a) => (
              <ListItem key={a.id} a={a} selected={selected?.id === a.id} onSelect={selectInModal} />
            ))}
            {past.length > 0 && (
              <p className="px-2 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Past</p>
            )}
            {past.map((a) => (
              <ListItem key={a.id} a={a} selected={selected?.id === a.id} onSelect={selectInModal} />
            ))}
            {announcements.length === 0 && (
              <p className="px-3 py-6 text-sm text-slate-400 text-center">No announcements yet.</p>
            )}
          </div>
        </aside>

        {/* Detail */}
        <section className="flex-1 min-w-0 flex flex-col">
          {selected ? (
            <>
              <div key={`hero-${selected.id}`} className={`relative px-6 pt-6 pb-5 bg-gradient-to-br ${lvl.hero} ksp-ann-shimmer overflow-hidden`}>
                <button
                  type="button"
                  onClick={closeModal}
                  className="absolute top-3 right-3 z-10 p-1.5 rounded-full text-white/80 hover:text-white hover:bg-black/20"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="relative z-10 ksp-ann-fade-up flex items-start gap-4 pr-8">
                  <span className="flex-shrink-0 w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-white shadow-inner">
                    <lvl.Icon className={`w-6 h-6 ${selected.level === 'urgent' ? 'ksp-ann-wiggle' : ''}`} />
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-white/90 bg-black/20 px-2 py-0.5 rounded-full">
                        {lvl.label}
                      </span>
                      {selected.is_pinned && (
                        <span className="flex items-center gap-1 text-[11px] font-semibold text-white/90 bg-black/20 px-2 py-0.5 rounded-full">
                          <Pin className="w-3 h-3" /> Pinned
                        </span>
                      )}
                      {!selected.is_live && (
                        <span className="text-[11px] font-semibold text-white/90 bg-black/30 px-2 py-0.5 rounded-full">Expired</span>
                      )}
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white leading-tight break-words">{selected.title}</h3>
                    <p className="mt-1.5 text-xs text-white/80">
                      {selected.created_by_name ? `Posted by ${selected.created_by_name} · ` : ''}
                      {formatDateTime(selected.published_at)}
                    </p>
                  </div>
                </div>
              </div>

              <div key={`body-${selected.id}`} className="ksp-ann-fade-up flex-1 overflow-y-auto px-6 py-5">
                {selected.body ? (
                  <div className="text-slate-200 leading-relaxed whitespace-pre-wrap break-words">
                    <Linkified text={selected.body} />
                  </div>
                ) : (
                  <p className="text-slate-400 italic">No additional details.</p>
                )}

                {selected.link_url && (
                  <a
                    href={selected.link_url}
                    target={selected.link_url.startsWith('/') ? undefined : '_blank'}
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-lg text-sm font-semibold text-white
                      bg-gradient-to-r ${lvl.hero} hover:brightness-110 shadow-lg shadow-black/30`}
                  >
                    {selected.link_label || 'Learn more'}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}

                {selected.expires_at && selected.is_live && (
                  <p className="mt-6 flex items-center gap-1.5 text-xs text-slate-400">
                    <CalendarClock className="w-3.5 h-3.5" />
                    Showing until {formatDateTime(selected.expires_at)}
                  </p>
                )}
              </div>

              <div className="px-6 py-3 border-t border-slate-700 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-1.5 rounded-lg text-sm font-medium bg-slate-700 text-slate-100 hover:bg-slate-600"
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-3">
              <button type="button" onClick={closeModal} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-700" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
              <Megaphone className="w-10 h-10 text-slate-600" />
              <p>No announcements yet.</p>
            </div>
          )}
        </section>
      </div>
    </div>,
    document.body
  );
};

export default AnnouncementModal;
